import * as z from 'zod';

export const ListPolicySnapshotsByBookingIdsRequest = z.object({
  bookingIds: z.array(z.string()).min(1).max(100),
});
export const ListPolicySnapshotsByBookingIdsResponse = z.object({
  bookingPolicySnapshots: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Booking policy snapshot ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookingId: z
          .string()
          .describe(
            "ID of the booking that's associated with this policy snapshot."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        policy: z
          .object({
            _id: z
              .string()
              .describe('Booking policy ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the booking policy is updated. To prevent conflicting changes, the current `revision` must be passed when updating the booking policy.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the booking policy was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the booking policy was updated.')
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Name of the booking policy.')
              .max(400)
              .optional()
              .nullable(),
            customPolicyDescription: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the description is displayed to the participant. `true` means the\ndescription is displayed.\n\nDefault: `false`'
                  )
                  .optional(),
                description: z
                  .string()
                  .describe(
                    'Description of the booking policy.\n\nDefault: Empty\nMax length: 2500 characters'
                  )
                  .max(2500)
                  .optional(),
                descriptionTranslated: z
                  .string()
                  .describe(
                    'Description of the translated booking policy.\n\nDefault: Empty\nMax length: 2500 characters'
                  )
                  .max(2500)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Custom description for the booking policy and whether the booking policy is displayed to the participant.'
              )
              .optional(),
            default: z
              .boolean()
              .describe('Whether the booking policy is the default.')
              .optional()
              .nullable(),
            cancellationPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether customers can cancel the booking. `true` means customers can cancel\nthe booking.\n\nDefault: `false`'
                  )
                  .optional(),
                limitLatestCancellation: z
                  .boolean()
                  .describe(
                    "Whether there's a limit on the latest cancellation time. `false` means\ncustomers can cancel the booking until the last minute before the course or\nsession starts.\n\nDefault: `false`"
                  )
                  .optional(),
                latestCancellationInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Minimum number of minutes before the start of the session customers can cancel.\nFor courses, this refers to the start of the first course session.\n\nDefault: `1440` minutes (1 day)\nMin: `1` minute'
                  )
                  .min(1)
                  .optional(),
              })
              .describe('Rule for canceling a booking.')
              .optional(),
            reschedulePolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether customers can reschedule a booking for an appointment-based service.\n`true` means customers can reschedule.\n\nDefault: `false`'
                  )
                  .optional(),
                limitLatestReschedule: z
                  .boolean()
                  .describe(
                    "Whether there's a limit on the latest supported rescheduling time. `false`\nmeans customers can reschedule until the last minute before the session start.\n\nDefault: `false`"
                  )
                  .optional(),
                latestRescheduleInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Minimum number of minutes before the session start session customers can\nreschedule their booking.\n\nDefault: `1440` minutes (1 day)\nMin: `1` minute'
                  )
                  .min(1)
                  .optional(),
              })
              .describe('Rule for rescheduling a booking.')
              .optional(),
            cancellationFeePolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether customers must pay a cancellation fee when canceling a booking.\n\nDefault: `false`'
                  )
                  .optional(),
                cancellationWindows: z
                  .array(
                    z.intersection(
                      z.object({
                        startInMinutes: z
                          .number()
                          .int()
                          .describe(
                            'Start of the cancellation window in minutes before the session start. For\ncourses, this refers to the start of the first course session.'
                          )
                          .min(1)
                          .optional()
                          .nullable(),
                      }),
                      z.xor([
                        z.object({
                          amount: z.never().optional(),
                          percentage: z.never().optional(),
                        }),
                        z.object({
                          percentage: z.never().optional(),
                          amount: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                                )
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                                )
                                .optional(),
                              formattedValue: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative.'
                                )
                                .max(50)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Fixed amount customers must pay when canceling the booking within this window.'
                            ),
                        }),
                        z.object({
                          amount: z.never().optional(),
                          percentage: z
                            .string()
                            .describe(
                              'Percentage of the booking price customers must pay when canceling within this window.\n\nMin: `0.01` percent\nMax: `100` percent'
                            ),
                        }),
                      ])
                    )
                  )
                  .max(2)
                  .optional(),
                autoCollectFeeEnabled: z
                  .boolean()
                  .describe(
                    'Whether Wix automatically charges the cancellation fee from the customer once\nthey cancel their booking.\n\nDefault: `true`'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Rules for cancellation fees.')
              .optional(),
            saveCreditCardPolicy: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether Wix stores credit card details of the customer. Storing the details\nallows Wix to prefill the checkout and thus increases the likelihood that the\ncustomer completes the booking process.\n\nDefault: `false`'
                  )
                  .optional(),
              })
              .describe('Rule for saving credit card details.')
              .optional(),
          })
          .describe(
            'Snapshot of the [booking policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction).\nat the time the corresponding booking was created.'
          )
          .optional(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the booking policy snapshot was created in `YYYY-MM-DDThh:mm:ssZ` format.'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
});
